import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    SimpleChanges,
    OnDestroy,
    AfterViewInit,
    ViewChild,
    ElementRef,
    NgZone,
    ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import {
    OrgDashboardWidget,
    OrgDashboardWidgetState,
    OrgWidgetData,
    OrgWidgetType,
    WIDGET_CONSTRAINTS,
    DEFAULT_ORG_WIDGETS,
} from '../models/org-widget.models';
import {
    canSeeWidget,
    scopeAcknowledgmentData,
    scopeFeatureMapData,
    scopeOverviewData,
    scopePolicyStatusData,
    visibleWidgetTypes,
} from '../scope/dashboard-scope';

// Widget components
import {
    OrgOverviewWidgetComponent,
    OverviewTileClick,
} from '../widgets/org-overview-widget/org-overview-widget.component';
import { OrgHierarchyWidgetComponent } from '../widgets/org-hierarchy-widget/org-hierarchy-widget.component';
import { RecentActivityWidgetComponent } from '../widgets/recent-activity-widget/recent-activity-widget.component';
import { SecurityCoverageWidgetComponent } from '../widgets/security-coverage-widget/security-coverage-widget.component';
import { PolicyStatusWidgetComponent } from '../widgets/policy-status-widget/policy-status-widget.component';
import { UserDirectoryWidgetComponent } from '../widgets/user-directory-widget/user-directory-widget.component';
import { PolicyAcknowledgmentWidgetComponent } from '../widgets/policy-acknowledgment-widget/policy-acknowledgment-widget.component';
import { FeatureMapWidgetComponent } from '../widgets/feature-map-widget/feature-map-widget.component';
import { OrgEntityColorsService } from '../services/org-entity-colors.service';
import {
    EntityColorPickerDialogComponent,
    EntityColorPickerDialogResult,
} from '../dialogs/entity-color-picker-dialog.component';

@Component({
    selector: 'shared-org-dashboard-grid',
    imports: [
        CommonModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatTooltipModule,
        MatCardModule,
        OrgOverviewWidgetComponent,
        OrgHierarchyWidgetComponent,
        RecentActivityWidgetComponent,
        SecurityCoverageWidgetComponent,
        PolicyStatusWidgetComponent,
        UserDirectoryWidgetComponent,
        PolicyAcknowledgmentWidgetComponent,
        FeatureMapWidgetComponent,
    ],
    templateUrl: './org-dashboard-grid.component.html',
    styleUrls: ['./org-dashboard-grid.component.scss'],
})
export class OrgDashboardGridComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @ViewChild('gridContainer', { static: true }) gridContainerRef!: ElementRef<HTMLElement>;

    @Input() widgetData!: OrgWidgetData;
    @Input() savedWidgets: OrgDashboardWidget[] | null = null;
    @Input() editMode = false;
    @Input() isAdmin = false;
    @Output() widgetsChanged = new EventEmitter<OrgDashboardWidget[]>();
    @Output() editModeChange = new EventEmitter<boolean>();
    @Output() tileClicked = new EventEmitter<OverviewTileClick>();
    @Output() entityColorsChanged = new EventEmitter<Record<string, string>>();

    public widgets: OrgDashboardWidgetState[] = [];
    /**
     * Per-widget views of `widgetData` for the current viewer (ISS-022). Rebuilt only when the input
     * changes, so a widget's `[data]` binding keeps a stable reference between change detections.
     */
    public scoped: {
        overview: OrgWidgetData;
        policyStatus: OrgWidgetData;
        acknowledgment: OrgWidgetData;
        featureMap: OrgWidgetData;
    } | null = null;
    public isDragging = false;
    public gridColumns = 4;

    /** Drag state */
    public dragIndex: number | null = null;
    public dropTargetIndex: number | null = null;
    private dragCloneEl: HTMLElement | null = null;
    private dragOffsetX = 0;
    private dragOffsetY = 0;
    private boundDragMove: ((e: PointerEvent) => void) | null = null;
    private boundDragEnd: (() => void) | null = null;

    /** Resize preview state */
    public resizePreviewColSpan = 0;
    public resizePreviewRowSpan = 0;
    public isResizingAny = false;
    public resizingWidgetId: string | null = null;

    /** Available widget types the user can add */
    public availableWidgetTypes: { type: OrgWidgetType; label: string; icon: string }[] = [
        { type: 'org-overview', label: 'Organization Overview', icon: 'analytics' },
        { type: 'org-hierarchy', label: 'Organization Structure', icon: 'account_tree' },
        { type: 'recent-activity', label: 'Recent Activity', icon: 'history' },
        { type: 'security-coverage', label: 'Security Coverage', icon: 'security' },
        { type: 'policy-status', label: 'Policy Status', icon: 'fact_check' },
        { type: 'user-directory', label: 'Staff Directory', icon: 'badge' },
        { type: 'policy-acknowledgment', label: 'Policy Acknowledgment', icon: 'verified' },
    ];

    private resizeObserver!: ResizeObserver;
    private observerAttached = false;

    constructor(
        private ngZone: NgZone,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        private colorsService: OrgEntityColorsService
    ) {}

    ngOnInit(): void {
        this.initWidgets();
        this.rescope();
    }

    /** Is this widget shown to the viewer? Hidden ones stay in the layout (and the saved record). */
    public isWidgetVisible(type: OrgWidgetType): boolean {
        return canSeeWidget(type, this.widgetData?.viewer);
    }

    /** Add-menu entries the viewer may add (staff never see admin-only widgets). A field, not a getter: it feeds an *ngFor. */
    public addableWidgetTypes: { type: OrgWidgetType; label: string; icon: string }[] = [];

    private rescope(): void {
        const d = this.widgetData;
        const allowed = new Set(
            visibleWidgetTypes(
                this.availableWidgetTypes.map((t) => t.type),
                d?.viewer
            )
        );
        this.addableWidgetTypes = this.availableWidgetTypes.filter((t) => allowed.has(t.type));
        this.scoped = d
            ? {
                  overview: scopeOverviewData(d),
                  policyStatus: scopePolicyStatusData(d),
                  acknowledgment: scopeAcknowledgmentData(d),
                  featureMap: scopeFeatureMapData(d),
              }
            : null;
    }

    ngAfterViewInit(): void {
        this.setupResponsiveGrid();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['widgetData']) this.rescope();
        if (changes['savedWidgets'] && !changes['savedWidgets'].firstChange) {
            // Don't reinitialize while user is actively editing, dragging, or resizing
            // — their live layout takes priority over external store updates.
            if (!this.editMode && !this.isDragging && !this.isResizingAny) {
                this.initWidgets();
            }
        }
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        // Clean up any active drag listeners
        if (this.boundDragMove) {
            document.removeEventListener('pointermove', this.boundDragMove);
        }
        if (this.boundDragEnd) {
            document.removeEventListener('pointerup', this.boundDragEnd);
        }
        this.dragCloneEl?.remove();
    }

    /** Open the entity color picker dialog from the widget header */
    public onOpenColorPicker(): void {
        const dialogRef = this.dialog.open(EntityColorPickerDialogComponent, {
            data: { currentColors: this.colorsService.colors() },
            width: '500px',
            maxHeight: '90vh',
            panelClass: 'entity-color-picker-panel',
        });

        dialogRef.afterClosed().subscribe((result: EntityColorPickerDialogResult | undefined) => {
            if (result) {
                this.colorsService.setColors(result.colors);
                this.entityColorsChanged.emit(result.colors as Record<string, string>);
            }
        });
    }

    /** Initialize widgets from saved state, or the defaults for a first-time user. */
    private initWidgets(): void {
        // Respect a saved layout EXACTLY. We previously appended any default widget
        // type missing from the save (to surface newly-shipped widgets), but that
        // re-added widgets the user had explicitly removed — a removed widget is
        // indistinguishable from a never-seen default. New widget types remain
        // available via the "+ Add" menu, so curation now wins.
        const source: OrgDashboardWidget[] = this.savedWidgets?.length ? this.savedWidgets : DEFAULT_ORG_WIDGETS;
        this.widgets = source.map((w) => this.toWidgetState(w)).sort((a, b) => a.layout.order - b.layout.order);
    }

    /** Convert persisted widget to runtime state */
    private toWidgetState(widget: OrgDashboardWidget): OrgDashboardWidgetState {
        return {
            ...widget,
            isResizing: false,
            constraints: WIDGET_CONSTRAINTS[widget.type] || {
                minColSpan: 1,
                maxColSpan: 4,
                minRowSpan: 1,
                maxRowSpan: 4,
            },
        };
    }

    /** Responsive grid columns based on container width */
    private setupResponsiveGrid(): void {
        const el = this.gridContainerRef?.nativeElement;
        if (!el) return;

        // Run outside zone so ResizeObserver doesn't trigger change detection continuously
        this.ngZone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const width = entry.contentRect.width;
                    let cols = 4;
                    if (width < 500) cols = 1;
                    else if (width < 800) cols = 2;
                    else if (width < 1200) cols = 3;

                    if (cols !== this.gridColumns) {
                        this.ngZone.run(() => {
                            this.gridColumns = cols;
                            this.cdr.markForCheck();
                        });
                    }
                }
            });
            this.resizeObserver.observe(el);
            this.observerAttached = true;
        });
    }

    // ──── Drag & Drop (manual pointer-based, CSS Grid compatible) ────

    /** Start drag from handle pointerdown (mouse + touch). */
    public onDragHandleDown(event: PointerEvent, index: number): void {
        if (!this.editMode) return;
        event.preventDefault();
        event.stopPropagation();

        this.dragIndex = index;
        this.isDragging = true;

        // Find the widget wrapper element
        const widgetEl = (event.target as HTMLElement).closest('.widget-wrapper') as HTMLElement;
        if (!widgetEl) return;

        const rect = widgetEl.getBoundingClientRect();
        this.dragOffsetX = event.clientX - rect.left;
        this.dragOffsetY = event.clientY - rect.top;

        // Create a floating clone for visual feedback
        const clone = widgetEl.cloneNode(true) as HTMLElement;
        clone.classList.add('drag-clone');
        clone.style.cssText = `
            position: fixed;
            width: ${rect.width}px;
            height: ${rect.height}px;
            left: ${rect.left}px;
            top: ${rect.top}px;
            z-index: 1000;
            pointer-events: none;
            opacity: 0.85;
            box-shadow: 0 8px 28px rgba(0,0,0,0.25);
            transform: rotate(1deg) scale(1.02);
            border-radius: 8px;
            overflow: hidden;
            transition: none;
        `;
        this.gridContainerRef.nativeElement.appendChild(clone);
        this.dragCloneEl = clone;

        // Prevent text selection while dragging
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';

        // Attach move/up listeners outside Angular zone for performance
        this.boundDragMove = (e: PointerEvent) => this.onDragPointerMove(e);
        this.boundDragEnd = () => this.onDragPointerUp();

        this.ngZone.runOutsideAngular(() => {
            if (this.boundDragMove) {
                document.addEventListener('pointermove', this.boundDragMove);
            }
            if (this.boundDragEnd) {
                document.addEventListener('pointerup', this.boundDragEnd);
            }
        });

        this.cdr.detectChanges();
    }

    /** Track pointer during drag — runs outside zone for performance */
    private onDragPointerMove(event: PointerEvent): void {
        // Move the floating clone to follow cursor
        if (this.dragCloneEl) {
            this.dragCloneEl.style.left = `${event.clientX - this.dragOffsetX}px`;
            this.dragCloneEl.style.top = `${event.clientY - this.dragOffsetY}px`;
        }

        // Determine which widget slot the cursor is over
        const target = this.findDropTarget(event.clientX, event.clientY);
        if (target !== this.dropTargetIndex) {
            this.dropTargetIndex = target;
            this.ngZone.run(() => this.cdr.detectChanges());
        }
    }

    /** Complete the drag — reorder widgets and clean up */
    private onDragPointerUp(): void {
        // Reorder array if we have a valid different target
        if (this.dragIndex !== null && this.dropTargetIndex !== null && this.dragIndex !== this.dropTargetIndex) {
            const [item] = this.widgets.splice(this.dragIndex, 1);
            this.widgets.splice(this.dropTargetIndex, 0, item);
            this.updateOrder();
            this.emitChanges();
        }

        // Remove floating clone
        this.dragCloneEl?.remove();
        this.dragCloneEl = null;

        // Reset drag state
        this.dragIndex = null;
        this.dropTargetIndex = null;
        this.isDragging = false;

        // Restore body styles
        document.body.style.userSelect = '';
        document.body.style.cursor = '';

        // Remove listeners
        if (this.boundDragMove) {
            document.removeEventListener('pointermove', this.boundDragMove);
        }
        if (this.boundDragEnd) {
            document.removeEventListener('pointerup', this.boundDragEnd);
        }
        this.boundDragMove = null;
        this.boundDragEnd = null;

        this.ngZone.run(() => this.cdr.detectChanges());
    }

    /** Find which widget index the cursor is hovering over */
    private findDropTarget(clientX: number, clientY: number): number | null {
        const gridEl = this.gridContainerRef?.nativeElement?.querySelector('.org-dashboard-grid');
        if (!gridEl) return null;

        const wrappers = gridEl.querySelectorAll(':scope > .widget-wrapper');
        for (let i = 0; i < wrappers.length; i++) {
            if (i === this.dragIndex) continue;
            const rect = wrappers[i].getBoundingClientRect();
            if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
                return i;
            }
        }
        return null;
    }

    // ──── Resize ────

    /** Handle resize drag on a widget corner (mouse + touch via pointer events).
        On phones gridColumns is 1, so the colSpan clamp below pins width to a
        single column and the gesture effectively resizes height (rowSpan) only. */
    public onResizeStart(widget: OrgDashboardWidgetState, event: PointerEvent): void {
        if (!this.editMode) return;
        event.preventDefault();
        event.stopPropagation();
        widget.isResizing = true;
        this.isResizingAny = true;
        this.resizingWidgetId = widget.id;
        this.resizePreviewColSpan = widget.layout.colSpan;
        this.resizePreviewRowSpan = widget.layout.rowSpan;

        const startX = event.clientX;
        const startY = event.clientY;
        const startColSpan = widget.layout.colSpan;
        const startRowSpan = widget.layout.rowSpan;

        // Measure actual cell size from grid
        const gridEl = this.gridContainerRef?.nativeElement?.querySelector('.org-dashboard-grid') as HTMLElement;
        const gridRect = gridEl?.getBoundingClientRect();
        const gap = 12; // matches scss gap
        const totalGapX = gap * (this.gridColumns - 1);
        const cellWidth = gridRect ? (gridRect.width - totalGapX) / this.gridColumns : 280;
        const cellHeight = 200; // approximate row height including gap

        const onMouseMove = (moveEvent: PointerEvent) => {
            const deltaX = moveEvent.clientX - startX;
            const deltaY = moveEvent.clientY - startY;

            const newColSpan = Math.min(
                widget.constraints.maxColSpan,
                Math.max(widget.constraints.minColSpan, startColSpan + Math.round(deltaX / cellWidth))
            );
            const newRowSpan = Math.min(
                widget.constraints.maxRowSpan,
                Math.max(widget.constraints.minRowSpan, startRowSpan + Math.round(deltaY / cellHeight))
            );

            const clampedCol = Math.min(newColSpan, this.gridColumns);

            // Update preview immediately
            this.resizePreviewColSpan = clampedCol;
            this.resizePreviewRowSpan = newRowSpan;

            // Apply live to widget
            widget.layout.colSpan = clampedCol;
            widget.layout.rowSpan = newRowSpan;
            this.cdr.detectChanges();
        };

        const onMouseUp = () => {
            widget.isResizing = false;
            this.isResizingAny = false;
            this.resizingWidgetId = null;
            document.removeEventListener('pointermove', onMouseMove);
            document.removeEventListener('pointerup', onMouseUp);
            this.emitChanges();
            this.cdr.detectChanges();
        };

        document.addEventListener('pointermove', onMouseMove);
        document.addEventListener('pointerup', onMouseUp);
    }

    // ──── Widget CRUD ────

    public isWidgetOnDashboard(type: OrgWidgetType): boolean {
        return this.widgets.some((w) => w.type === type);
    }

    public addWidget(type: OrgWidgetType): void {
        // One instance per widget type — the Add menu shows a check on types that
        // are already present, so a repeat click is a no-op (no duplicates).
        if (this.isWidgetOnDashboard(type)) return;
        if (!this.isWidgetVisible(type)) return;

        const typeConfig = this.availableWidgetTypes.find((t) => t.type === type);
        const newWidget: OrgDashboardWidget = {
            id: `${type}-${Date.now()}`,
            type,
            title: typeConfig?.label || type,
            layout: {
                colSpan: type === 'org-overview' ? 4 : 2,
                rowSpan: type === 'org-overview' ? 1 : 2,
                order: this.widgets.length,
            },
        };
        this.widgets.push(this.toWidgetState(newWidget));
        this.emitChanges();
    }

    public removeWidget(widget: OrgDashboardWidgetState): void {
        this.widgets = this.widgets.filter((w) => w.id !== widget.id);
        this.updateOrder();
        this.emitChanges();
    }

    public toggleEditMode(): void {
        this.editMode = !this.editMode;
        this.editModeChange.emit(this.editMode);
    }

    // ──── Helpers ────

    private updateOrder(): void {
        this.widgets.forEach((w, i) => (w.layout.order = i));
    }

    private emitChanges(): void {
        // Strip runtime state and emit persisted format
        const persisted: OrgDashboardWidget[] = this.widgets.map((w) => ({
            id: w.id,
            type: w.type,
            title: w.title,
            layout: { ...w.layout },
        }));
        this.widgetsChanged.emit(persisted);
    }

    /** Get effective column span (clamped to available grid columns) */
    public getEffectiveColSpan(widget: OrgDashboardWidgetState): number {
        return Math.min(widget.layout.colSpan, this.gridColumns);
    }

    /** Track function for ngFor */
    public trackByWidgetId(_index: number, widget: OrgDashboardWidgetState): string {
        return widget.id;
    }

    /** Get resize badge text */
    public getResizeBadge(widget: OrgDashboardWidgetState): string {
        return `${widget.layout.colSpan} × ${widget.layout.rowSpan}`;
    }
}
